import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Optional } from '@gov-nx/core/types';
import { isObject } from '@gov-nx/core/types';
import { useFiltersStore, useFiltersStoreFilters } from '@gov-nx/store';
import {
	hasFilterChanges as hasFilterChangesUtil,
	normalizePersistedFilterWithMigration,
} from './utils';
import type { HasFilterChangesOptions } from './utils.types';

type Serialized<T> = {
	[P in keyof T]: undefined extends T[P]
		? // eslint-disable-next-line @typescript-eslint/no-explicit-any
			Optional<any>
		: T[P] extends Date
			? string
			: T[P];
};

interface FilterProps<T> {
	key: string;
	initialState: T;
	rehydrate?: (data: Serialized<T>) => T;
	persist?: boolean;
}

export const useFilter = <T extends object>({
	key,
	initialState,
	rehydrate,
	persist = true,
}: FilterProps<T>) => {
	const updateFiltersAction = useFiltersStore('updateFiltersAction');
	const updateFiltersWithPathAction = useFiltersStore(
		'updateFiltersWithPathAction'
	);
	const filter = useFiltersStoreFilters<Serialized<T>>(key);
	const [filterAsState, setFilterAsState] = useState<T>(initialState);

	const setPartialFilter = useCallback(
		(newFilter: Partial<T>) => {
			if (!persist) {
				setFilterAsState((prevFilter) => ({
					...prevFilter,
					...newFilter,
				}));
				return;
			}

			updateFiltersAction(key, newFilter);
		},
		[key, persist, updateFiltersAction]
	);

	const setPartialFilterWithPath = <Path extends keyof T>(
		newFilter: Partial<T[Path]>,
		path: Path
	) => {
		if (!persist) {
			setFilterAsState((prevFilter) => {
				const currentPathFilter = prevFilter[path];
				if (!isObject(currentPathFilter) || !isObject(newFilter)) {
					return prevFilter;
				}

				return {
					...prevFilter,
					[path]: {
						...currentPathFilter,
						...newFilter,
					},
				};
			});
			return;
		}

		updateFiltersWithPathAction([key, path as string], newFilter);
	};

	useEffect(() => {
		if (!persist) {
			return;
		}

		if (filter === undefined) {
			updateFiltersAction(key, initialState);
		}
	}, [filter, initialState, key, persist, updateFiltersAction]);

	const normalizedFilterResult = useMemo(
		() =>
			filter
				? normalizePersistedFilterWithMigration<Serialized<T>>(
						key,
						initialState as Serialized<T>,
						filter
					)
				: undefined,
		[filter, initialState, key]
	);

	useEffect(() => {
		if (!persist || !filter || !normalizedFilterResult?.migrationPatch) {
			return;
		}

		updateFiltersAction(
			key,
			normalizedFilterResult.migrationPatch as unknown as Partial<T>
		);
	}, [filter, key, normalizedFilterResult, persist, updateFiltersAction]);

	const normalizedFilter = normalizedFilterResult?.normalized;

	const resolvedFilter =
		rehydrate && normalizedFilter
			? rehydrate(normalizedFilter)
			: (normalizedFilter ?? initialState);
	const currentFilter = persist ? resolvedFilter : filterAsState;

	const hasFilterChanges = useCallback(
		(options: HasFilterChangesOptions<T> = {}) =>
			hasFilterChangesUtil({
				currentFilter,
				initialState,
				...options,
			}),
		[currentFilter, initialState]
	);

	return {
		filter: currentFilter,
		setPartialFilter,
		setPartialFilterWithPath,
		hasFilterChanges,
		reset: () => {
			if (!persist) {
				setFilterAsState(initialState);
				return;
			}

			updateFiltersAction(key, initialState);
		},
	};
};
